import React, { useState } from 'react';
import { toast } from 'react-toastify';
import DatePickerField from './DatePickerField';
import '../../pages/monitoring/Monitoring.css';

const SEVERITY_OPTIONS = ['critical', 'major', 'minor'];
const STATUS_OPTIONS = ['not_done', 'partially_done', 'done'];

export const FINDING_STATUS_LABEL = {
  not_done: 'Not Done',
  partially_done: 'Partially Done',
  done: 'Done',
};

/**
 * Create or edit a ComplianceFinding - the real, backend-tracked record
 * behind "Risks & Issues" on the project page. `finding` present means
 * edit mode (status/corrective action can be updated); omitted means a new
 * issue is being logged.
 */
const FindingFormModal = ({ isOpen, onClose, onSave, saving, categories, finding = null }) => {
  const isEdit = !!finding;
  const [formData, setFormData] = useState({
    category: finding?.category || categories[0]?.id || '',
    title: finding?.title || '',
    description: finding?.description || '',
    severity: finding?.severity || 'major',
    status: finding?.status || 'not_done',
    correctiveAction: finding?.correctiveAction || '',
    deadline: finding?.deadline || '',
  });

  const handleChange = (field) => (e) => {
    const value = e.target ? e.target.value : e;
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      toast.error('Please enter a title describing the issue');
      return;
    }
    if (!formData.category) {
      toast.error('Please select a category');
      return;
    }
    onSave(formData);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="monitoring-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2><i className="fas fa-exclamation-triangle"></i> {isEdit ? 'Issue Details' : 'Log Issue'}</h2>
          <button className="close-btn" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-content">
            <div className="form-grid">
              <div className="form-group full-width">
                <label>Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={handleChange('title')}
                  placeholder="e.g. Inadequate PPE availability"
                  required
                />
              </div>

              <div className="form-group">
                <label>Category *</label>
                <select value={formData.category} onChange={handleChange('category')} required>
                  <option value="">Select category...</option>
                  {categories.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                </select>
              </div>

              <div className="form-group">
                <label>Severity *</label>
                <select value={formData.severity} onChange={handleChange('severity')} required>
                  {SEVERITY_OPTIONS.map((s) => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                </select>
              </div>

              {isEdit && (
                <div className="form-group">
                  <label>Status</label>
                  <select value={formData.status} onChange={handleChange('status')}>
                    {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{FINDING_STATUS_LABEL[s]}</option>)}
                  </select>
                </div>
              )}

              <div className="form-group">
                <label>Deadline</label>
                <DatePickerField value={formData.deadline} onChange={handleChange('deadline')} />
              </div>

              <div className="form-group full-width">
                <label>Description</label>
                <textarea
                  rows="3"
                  value={formData.description}
                  onChange={handleChange('description')}
                  placeholder="What did you observe?"
                />
              </div>

              <div className="form-group full-width">
                <label>Corrective Action</label>
                <textarea
                  rows="3"
                  value={formData.correctiveAction}
                  onChange={handleChange('correctiveAction')}
                  placeholder="What needs to be done to fix this?"
                />
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn-cancel" onClick={onClose} disabled={saving}>Cancel</button>
            <button type="submit" className="btn-primary-green" disabled={saving}>
              {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Log Issue'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FindingFormModal;
